import React, { useState } from 'react';
import { StarButton } from '../../components/StarButton';
import { useProject } from '../../hooks/useProject';
import { useProjectIdFromRoute } from '../../hooks/useProjectIdFromRoute';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { navigateToAngular } from '../../services/navigate';
import { hasPermission } from '../../types/project';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { NewCollectionModal } from '../ProjectDetailPage/NewCollectionModal';

export function GetStartedPage() {
  const projectId = useProjectIdFromRoute();
  const { data: project, isLoading, isError } = useProject(projectId);
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);

  useProjectPageMount(project, 'projects', 'getstarted');

  if (isLoading) return <LoadingPlaceholder />;
  if (isError || !project) return <div className="alert alert-danger">Failed to load project.</div>;

  const canManageCollections = hasPermission(project, 'MANAGE_COLLECTIONS');

  return (
    <>
    <div>
      <div className="page-header">
        <h1><StarButton project={project} /> {project.name}</h1>
      </div>

      <ul className="breadcrumb">
        <li><a onClick={() => navigateToAngular('/project')} style={{ cursor: 'pointer' }}>Projects</a></li>
        <li><a onClick={() => navigateToAngular(`/project/${project.id}`)} style={{ cursor: 'pointer' }}>{project.name}</a></li>
        <li className="active">Get Started</li>
      </ul>

      <h2>Let's get you started with your project.</h2>

      <div className="row">
        <div className="col-md-6">
          <div className="well">
            {canManageCollections && (
              <p>
                <button className="btn btn-primary btn-lg" onClick={() => setAddCollectionOpen(true)}>
                  Add data to this project <i className="fas fa-plus-circle" />
                </button>
              </p>
            )}
            <p>Once you get some data uploaded, Indexed I/O will extract metadata and full text from your data. You'll then be able to search, tag and export your data!</p>
          </div>
        </div>
      </div>

      <iframe
        width="560"
        height="315"
        src="https://www.youtube.com/embed/K8XyL5b9k8Q"
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Getting started with Indexed I/O"
      />

      <h2>Still need some help?</h2>
      <p className="lead">
        Check out our awesome <a target="_blank" rel="noreferrer" href="https://indexedio.zendesk.com/hc/">support</a>,
        or keep reading for a more detailed explanation.
      </p>

      <div className="row">
        <div className="col-md-4">
          <div className="well well-small">
            <h3><i className="fas fa-gavel" /> Projects</h3>
            <p>You probably guessed this one, but your Project is your top level organizational unit which contains one or many Collections.</p>
            <h5>Project highlights</h5>
            <ul>
              <li>You can invite other people to help use (or administer) your project</li>
              <li>All Collections, Custodians, Users etc all live under a Project</li>
              <li>Each Project is billed individually</li>
              <li>Each Project gets its own protected SFTP site for uploads</li>
            </ul>
            <hr />
            <p style={{ textAlign: 'center' }}>
              <a onClick={() => navigateToAngular(`/project/${project.id}/admin`)} className="btn btn-default" style={{ cursor: 'pointer' }}>
                View Project Admin <i className="fas fa-cog" />
              </a>
            </p>
          </div>
        </div>

        <div className="col-md-4">
          <div className="well well-small">
            <h3><i className="fas fa-hdd" /> Collections</h3>
            <p>Collections are groups of data that belong to an individual (or Custodian).</p>
            <h5>Collection highlights</h5>
            <ul>
              <li>There is no limit to the size of your collection (unless it's a trial project)</li>
              <li>Collections can be processed individually, or all at once</li>
              <li>You can do all kinds of neat things like run reports, export and search through your Collections.</li>
            </ul>
            <hr />
            {canManageCollections && (
              <p style={{ textAlign: 'center' }}>
                <button className="btn btn-primary" onClick={() => setAddCollectionOpen(true)}>
                  Add a Collection <i className="fas fa-plus-circle" />
                </button>
              </p>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="well well-small">
            <h3><i className="fas fa-user" /> Custodians</h3>
            <p>Custodians are people like you and me who have had their data collected (into a Collection).</p>
            <h5>Custodian highlights</h5>
            <ul>
              <li>You create Custodians as you create your Collections</li>
              <li>You can generate reports with all kinds of data analytics between custodians</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    {addCollectionOpen && (
      <NewCollectionModal project={project} onClose={() => setAddCollectionOpen(false)} />
    )}
  </>
  );
}
